import { Button, buttonVariants } from '@/components/Button';
import { cn } from '@/lib/cn';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as SelectPrimitive from '@radix-ui/react-select';
import { VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { forwardRef } from 'react';

/**
 * Renders a select component that can be used with react-hook-form.
 *
 * @example
 * ```tsx
 * <Select onValueChange={(value) => setSelectValue(value)} value={selectValue}>
 *   <Select.TriggerButton>
 *     <Select.Value placeholder="Select an option" />
 *   </Select.TriggerButton>
 *   <Select.Content>
 *     <Select.Group>
 *       <Select.Label>Label</Select.Label>
 *       <Select.Item value="item1">Item 1</Select.Item>
 *       <Select.Item value="item2">Item 2</Select.Item>
 *     </Select.Group>
 *   </Select.Content>
 * </Select>
 * ```
 */
export const Select = ({ children, ...props }: SelectPrimitive.SelectProps) => {
    return <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>;
};

/**
 * Renders the select popover container in a portal.
 *
 * @example
 * ```tsx
 * <Select name="select" onValueChange={(value) => console.log(value)}>
 *   <Select.Trigger asChild>
 *     <Select.Value placeholder="Select an option" />
 *   </Select.Trigger>
 *   <Select.Content>
 *     <Select.Group>
 *       <Select.Label>Label</Select.Label>
 *       <Select.Item value="item1">Item 1</Select.Item>
 *       <Select.Item value="item2">Item 2</Select.Item>
 *     </Select.Group>
 *   </Select.Content>
 * </Select>
 * ```
 */
const SelectContent = ({
    className,
    children,
    position = 'popper',
    sideOffset = 2,
    container,
    ...props
}: SelectPrimitive.SelectContentProps & {
    container?: HTMLElement | null;
}) => (
    <SelectPrimitive.Portal container={container}>
        <SelectPrimitive.Content
            className={cn(
                'relative text-textPrimary bg-componentBackgroundPrimary min-w-[var(--radix-select-trigger-width)] overflow-hidden animate-none p-xxxs z-30 max-h-96 border border-outlinePrimary',
                className
            )}
            position={position}
            sideOffset={sideOffset}
            {...props}
        >
            <SelectPrimitive.Viewport className='w-full !scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary'>
                {children}
            </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
);

/**
 * Renders a separator between select items.
 *
 * @example
 * ```tsx
 * <Select.Item>
 *   Item 1
 * </Select.Item>
 * <Select.Separator />
 * <Select.Item>
 *   Item 2
 * </Select.Item>
 * ```
 */
const SelectSeparator = forwardRef<
    React.ElementRef<typeof SelectPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
        ref={ref}
        className={clsx('my-xxxs mx-xxxs h-px bg-dividerPrimary', className)}
        {...props}
    />
));

/**
 * Group select options together.
 * A separator is automatically added between groups.
 *
 * @example
 * ```tsx
 * <Select.Group>
 *   <Select.Item value='option1'>
 *     Option 1
 *  </Select.Item>
 *  <Select.Item value='option2'>
 *    Option 2
 *  </Select.Item>
 * </Select.Group>
 */
const SelectGroup = forwardRef<
    React.ElementRef<typeof SelectPrimitive.Group>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Group ref={ref} className={cn('group/group mt-xxxs last:my-xxxs', className)} {...props}>
        {children}
        <SelectSeparator className='my-xxxs group-last/group:hidden' />
    </SelectPrimitive.Group>
));

/**
 * Renders a select item, that when clicked triggers the item to be selected.
 *
 * @example
 * ```tsx
 * <Select.Item value="item-1">
 *   Item 1
 * </Select.Item>
 * <Select.Item value="item-2">
 *   Item 2
 * </Select.Item>
 * ```
 */
export const SelectItem = forwardRef<
    React.ElementRef<typeof SelectPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={clsx(
            'relative flex mx-xxxs cursor-pointer select-none items-center px-xs py-autocomplete rounded-sm text-sm outline-none transition-colors hover:bg-dividerTertiary focus:bg-dividerTertiary data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[state=checked]:bg-dividerTertiary data-[state=checked]:font-semibold',
            className
        )}
        {...props}
    >
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
));

/**
 * Renders an non-interactable label for the select component.
 *
 * @example
 * ```tsx
 * <Select.Group>
 *   <Select.Label>Label</Select.Label>
 *   <Select.Item value="item1">Item 1</Select.Item>
 *   <Select.Item value="item2">Item 2</Select.Item>
 * </Select.Group>
 */
const SelectLabel = forwardRef<
    React.ElementRef<typeof SelectPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label ref={ref} className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)} {...props} />
));

/**
 * Renders a button that triggers opening the select dropdown.
 *
 * Use asChild to render a custom element.
 *
 * @example
 * ```tsx
 * <Select>
 *   <Select.Trigger asChild>
 *      <button>
 *       <Select.Value placeholder="Select an option" />
 *      </button>
 *  </Select.Trigger>
 *  ...
 * </Select>
 * ```
 */
const SelectTrigger = forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ children, ...props }, ref) => (
    <SelectPrimitive.Trigger {...props} ref={ref}>
        {children}
    </SelectPrimitive.Trigger>
));

/**
 * Renders a button that triggers the select dropdown.
 *
 * @example
 * ```tsx
 * <Select.TriggerButton>
 *   <Select.Value placeholder='Select an option' />
 * </Select.TriggerButton>
 */
const SelectTriggerButton = forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
        variant?: VariantProps<typeof buttonVariants>['variant'];
    }
>(({ className, children, variant = 'secondary', ...props }, ref) => (
    <SelectTrigger ref={ref} asChild {...props}>
        <Button
            variant={variant}
            className={cn('flex items-center gap-x-2 pr-3.5 data-[placeholder]:text-textIncomplete', className)}
        >
            {children}
            <SelectPrimitive.Icon asChild>
                <FontAwesomeIcon icon={faChevronDown} className='ml-auto size-[12px]' />
            </SelectPrimitive.Icon>
        </Button>
    </SelectTrigger>
));

/**
 * Renders the currently selected value of the select component.
 *
 * @example
 * ```tsx
 * <Select.Trigger asChild>
 *    <Select.Value placeholder="Select an option" />
 * </Select.Trigger>
 * ```
 */
const SelectValue = SelectPrimitive.Value;

export default Object.assign(Select, {
    Content: SelectContent,
    Trigger: SelectTrigger,
    Group: SelectGroup,
    Item: SelectItem,
    Label: SelectLabel,
    Separator: SelectSeparator,
    TriggerButton: SelectTriggerButton,
    Value: SelectValue
});
